import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type AdminDashboardPeriod = '7d' | '30d' | '12m';

export interface AdminKPIs {
  total_users: number;
  active_users: number;
  total_shops: number;
  active_shops: number;
  active_subscriptions: number;
  pending_subscriptions: number;
  monthly_revenue: number;
  previous_month_revenue: number;
  churn_count: number;
  churn_rate: number;
}

export interface TrendItem {
  month: string;
  total?: number;
  count?: number;
}

export interface PendingSubscription {
  id: string;
  status: string;
  created_at: string;
  user: { id: string; name: string; email: string };
  plan: { id: string; display_name: string };
}

export interface PendingQuoteRequest {
  id: string;
  status: string;
  created_at: string;
  company_name: string;
  business_sector: string;
  expected_shops: number;
  user: { id: string; name: string; email: string } | null;
}

export interface SystemAlert {
  type: string;
  severity: 'warning' | 'error' | 'info';
  message: string;
  count: number;
  timestamp: string;
}

export interface AdminDashboardSummary {
  status_overview: 'healthy' | 'warning' | 'critical';
  revenue_period_total: number;
  revenue_previous_period_total: number;
  revenue_growth_percent: number;
}

export interface AiTokenUsage {
  total_input_tokens: number;
  total_output_tokens: number;
  total_messages: number;
  estimated_cost_usd: number;
  budget_limit_usd: number | null;
  budget_used_percent: number | null;
  monthly_input_tokens: number;
  monthly_output_tokens: number;
  monthly_cost_usd: number;
}

export interface AiTokenTrendItem {
  bucket: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

export interface TopShop {
  shop_id: string;
  shop_name: string;
  total: number;
}

export interface ConversionMetrics {
  free_count: number;
  paid_count: number;
  total_count: number;
  conversion_rate: number;
}

export interface AdminDashboardData {
  period: AdminDashboardPeriod;
  kpis: AdminKPIs;
  summary: AdminDashboardSummary;
  revenue_trend: TrendItem[];
  registration_trend: TrendItem[];
  pending_subscriptions: PendingSubscription[];
  queues: {
    quote_requests_pending: PendingQuoteRequest[];
  };
  sync_health: {
    failed_24h: number;
    unresolved_conflicts: number;
    stale_devices_7d: number;
  };
  system_alerts: SystemAlert[];
  ai_token_usage: AiTokenUsage;
  ai_token_trend: AiTokenTrendItem[];
  top_shops?: TopShop[];
  conversion?: ConversionMetrics;
  last_updated_at: string;
}

function normalizePeriod(value: AdminDashboardPeriod): AdminDashboardPeriod {
  return ['7d', '30d', '12m'].includes(value) ? value : '30d';
}

export function useAdminDashboard(period: AdminDashboardPeriod = '30d') {
  const safePeriod = normalizePeriod(period);

  return useQuery({
    queryKey: ['admin', 'dashboard', safePeriod],
    queryFn: async () => {
      const response = await api.get<AdminDashboardData>(`/admin/dashboard?period=${safePeriod}`);
      return response.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data as AdminDashboardData | undefined;
      if (data?.summary?.status_overview === 'critical') return 30_000;
      return 60_000;
    },
  });
}
